import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { BasicTableComponent } from '@common/references/basic-table.component';
import { Booking, BookingStatus } from '@modules/universal-billing/accounting/bookings/bookings.interfaces';
import { MoneyFormatPipe } from '@common/pipe/money-format.pipe';
import { PTableCellValueComponent } from '@primeng/table/components/p-table-cell-value/p-table-cell-value.component';
import { Table } from '@primeng/table';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass, NgIf } from '@angular/common';
import { BookingsService } from '@modules/universal-billing/accounting/bookings/bookings.service';
import { CalendarModule } from '@primeng/calendar';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SkeletonModule } from '@primeng/skeleton';
import {
  BookingAssignComponent
} from '@modules/universal-billing/accounting/bookings/booking-assign/booking-assign.component';
import {
  BookingAssignService
} from '@modules/universal-billing/accounting/bookings/booking-assign/booking-assign.service';
import { ModalClose } from '@shared/services/modal/modal.enum';
import {
  BookingImportComponent
} from '@modules/universal-billing/accounting/bookings/booking-import/booking-import.component';
import { SidebarModule } from '@shared/components/sidebar/sidebar.module';
import { SidebarService } from '@shared/components/sidebar/sidebar.service';
import { debounceTime, Observable } from 'rxjs';
import { PageResult } from '@shared/components/classes/page-result';
import { formatDate } from '@shared/utils/format-date';
import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import urlJoin from 'url-join';
import { environment } from '@environments/environment';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { KeenIconComponent } from '@shared/components/keen-icon/keen-icon.component';


@Component({
  selector: 'app-bookings',
  standalone: true,
  imports: [
    MoneyFormatPipe,
    PTableCellValueComponent,
    Table,
    TranslatePipe,
    NgClass,
    CalendarModule,
    InputValidatorDirective,
    ReactiveFormsModule,
    SkeletonModule,
    FormsModule,
    BookingAssignComponent,
    SidebarModule,
    NgIf,
    PageTitleComponent,
    KeenIconComponent,
  ],
  templateUrl: './bookings.component.html',
  styleUrl: './bookings.component.scss'
})
export class BookingsComponent extends BasicTableComponent<Booking> implements OnInit {

  selectedBooking: Booking | null = null;
  service = inject(BookingsService);
  bookingAssignService = inject(BookingAssignService);
  searchInput: string | null = null;
  @ViewChild('dt') dt: Table;
  @Input() hideActions: boolean = false;
  breadCrumbItems!: Array<{}>;
  selectedAssignment: any = null
  http = inject(HttpClient);
  public filters = new FormGroup({
    search: new FormControl(),
    account: new FormControl(),
    amount: new FormControl(),
    documentNumber1: new FormControl(),
    dateRange: new FormControl(),
  })
  public sidebarName = 'booking-assign';
  public sidebarIsOpened = false;
  currentDateRange: any=null;
  protected sidebarService = inject(SidebarService);
  protected readonly BookingStatus = BookingStatus;


  getAll(params: any): Observable<PageResult<any>> {
    const {search, documentNumber1, amount, account, dateRange} = this.filters.value;
    if (dateRange) {
      this.currentDateRange = dateRange
      params.dateFrom = formatDate(dateRange[0]);
      params.dateTo = formatDate(dateRange[1]);
      params.date_column = 'booking_date'
    }
    params.search = search;
    params.amount = amount;
    params.documentNumber1 = documentNumber1
    params.account = account

    return super.getAll(params);
  }


  ngOnInit(): void {
    this.breadCrumbItems = [
      {label: 'MENU.ACCOUNTING', iconClass: 'las la-settings'},
      {label: this.service.title, active: true}
    ];
    super.ngOnInit();

    this.filters.valueChanges.pipe(debounceTime(1000)).subscribe(() => this.loadData());

  }

  onSelectBooking(booking: any) {
    this.selectedBooking = booking;
    const id = booking?.bookingDocument?.id
    if (id != null) {
      this.bookingAssignService.getOne(id).subscribe(
        {
          next: data => {
            this.selectedAssignment = data;
            this.openSidebar();
          },
          error: err => {
            this.selectedAssignment = null;
            this.openSidebar();
          }
        }
      )
    } else {
      this.selectedAssignment = null;
      this.openSidebar();
    }
  }

  onAdd() {
    this.modalService.open({
      component: BookingImportComponent,
      title: this.translateService.instant('BOOKINGS.IMPORT'),
      componentOptions: {},
      modalOptions: {
        size: 'lg',
      }
    }).then(value => {
      if (value == ModalClose.SUCCESS) {
        this.loadData();
      }
    })
  }

  export() {
    const dateRange = this.currentDateRange;
    if (dateRange && dateRange[0] != null && dateRange[1] != null) {

      const params = new HttpParams()
        .set('fromDate', formatDate(dateRange[0]))
        .set('toDate', formatDate(dateRange[1]));
      return this.http.get(
        urlJoin(environment.apiUrl, 'datevbookings/export'),
        {params, responseType: 'blob', observe: 'response'}
      ).subscribe({
        next: (res) => {
          this.saveBlob(res.body!, this.filenameFrom(res));
          this.sweetAlertService.success('', "DOCUMENT.SUCCESS")
        },
        error: (err: HttpErrorResponse) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result as string);
              this.sweetAlertService.error('', json.message);
            } catch {
              this.sweetAlertService.error('', 'DOCUMENT.ERROR_MSG');
            }
          };
          reader.readAsText(err.error);
        }
      });
    }
  }

  openSidebar() {
    if (this.sidebarIsOpened) {
      return;
    }
    this.sidebarIsOpened = true;
    this.sidebarService.getSidebarRegistry(this.sidebarName).toggleOpen();
  }

  closeSidebar() {
    if (!this.sidebarIsOpened) {
      return;
    }
    this.sidebarIsOpened = false;
    this.sidebarService.getSidebarRegistry(this.sidebarName).toggleOpen();
  }


  private filenameFrom(res: HttpResponse<Blob>): string {
    const cd = res.headers.get('Content-Disposition') ?? '';
    const m = /filename="?([^"]+)"?/i.exec(cd);
    return m?.[1] ?? 'export.csv';
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
  }
}
